import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { contractorAuth } from '../lib/contractorAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Ticket templates: Ticket Mgmt -> Templates for staff, and (088) the
 * contractor portal's Templates tab for a contractor an administrator has
 * allowed to manage them (Settings -> Contractors, "May manage ticket
 * templates"). Gordon, 26 October: templates stay in Ticket Mgmt, and a
 * contractor with the permission sees and edits them from the portal. The
 * server checks the permission on every request; this only draws what it
 * is answered.
 *
 * `portal` is the contractor's view: every call signed with their own token
 * (contractorAuth - the staff interceptor has nothing to attach for them),
 * the approver picker drawn from /auth/approvers (the firm's approvers, which
 * a contractor may read; /users is administrators only), and no "Use
 * template", which opens the staff Create Ticket form.
 */
const blankTemplate = { name: '', description: '', categoryId: '', priorityId: '', defaultApproverId: '' };

const TicketTemplates = ({ onSelectTemplate, portal = false }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState(blankTemplate);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);
  // What a save or retire refused with, said on the page rather than in an
  // alert() over the form.
  const [notice, setNotice] = useState(null);
  const [refused, setRefused] = useState(null);
  // Retiring is asked in the card, not in a window.confirm() over the page.
  const [retiring, setRetiring] = useState(null);

  const cfg = useCallback(() => (portal ? contractorAuth() : undefined), [portal]);

  const fetchTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/templates`, cfg());
      setTemplates(Array.isArray(r.data) ? r.data : []);
      setRefused(null);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 503) {
        setRefused(err.response?.data?.error || 'Ticket templates are not open to you.');
      } else {
        setNotice(err.response?.data?.error || 'Could not load the templates.');
      }
    } finally {
      setLoading(false);
    }
  }, [cfg]);

  const loadOptions = useCallback(async () => {
    const get = (path) => axios.get(`${API_BASE}${path}`, cfg()).then((r) => r.data).catch(() => []);
    const [cats, pris, apps] = await Promise.all([get('/categories'), get('/priorities'), get('/auth/approvers')]);
    setCategories(Array.isArray(cats) ? cats : []);
    setPriorities(Array.isArray(pris) ? pris : []);
    setApprovers(Array.isArray(apps) ? apps : []);
  }, [cfg]);

  useEffect(() => {
    fetchTemplates();
    loadOptions();
  }, [fetchTemplates, loadOptions]);

  // The form's own names, sent as the API's. This posted the camelCase
  // fields as they were, which the API never read - a template created here
  // saved its name and description and lost its category, priority and
  // approver every time.
  const bodyOf = (t) => ({
    name: t.name.trim(),
    description: t.description,
    category_id: t.categoryId || null,
    priority_id: t.priorityId || null,
    default_approver_id: t.defaultApproverId || null
  });

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) { setNotice('A template name is required'); return; }
    try {
      setNotice(null);
      await axios.post(`${API_BASE}/templates`, bodyOf(newTemplate), cfg());
      setNewTemplate(blankTemplate);
      setShowCreateForm(false);
      fetchTemplates();
    } catch (err) {
      setNotice(err.response?.data?.error || 'Failed to create template');
    }
  };

  // This called onSelectTemplate, a prop the route never passes - so clicking
  // Use Template threw and nothing happened. It now opens the create form with
  // the template applied.
  const handleUseTemplate = (templateId) => {
    if (typeof onSelectTemplate === 'function') { onSelectTemplate(templateId); return; }
    navigate(`/create?template=${encodeURIComponent(templateId)}`);
  };

  const startEdit = (template) => {
    setEditing({
      id: template.id,
      name: template.name || '',
      description: template.description || '',
      categoryId: template.category_id || '',
      priorityId: template.priority_id || '',
      defaultApproverId: template.default_approver_id || ''
    });
  };

  const saveEdit = async () => {
    if (!editing.name.trim()) { setNotice('A template name is required'); return; }
    try {
      setNotice(null);
      await axios.put(`${API_BASE}/templates/${editing.id}`, bodyOf(editing), cfg());
      setEditing(null);
      fetchTemplates();
    } catch (error) {
      setNotice(error.response?.data?.error || 'Could not save that template');
    }
  };

  // The API deactivates rather than deletes, so the wording says so.
  const retire = async (template) => {
    setRetiring(null);
    try {
      await axios.delete(`${API_BASE}/templates/${template.id}`, cfg());
      setNotice(null);
      fetchTemplates();
    } catch (error) {
      setNotice(error.response?.data?.error || 'Could not retire that template');
    }
  };

  // Was a fixed hex used as a background with near-white text, from a retired
  // palette - High measured 2.01:1. The themed .priority-badge variants in
  // App.css carry the right pair for both themes.
  const prioritySlug = (priorityName) =>
    String(priorityName || '').trim().toLowerCase();

  const approverName = (u) => u.display_name || u.name || u.email;

  if (refused) {
    return (
      <div className="ticket-templates">
        <div className="templates-header"><h2>Ticket Templates</h2></div>
        <p className="no-templates" role="status">{refused}</p>
      </div>
    );
  }

  return (
    <div className="ticket-templates" data-portal={portal || undefined}>
      <div className="templates-header">
        <h2>Ticket Templates</h2>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
          aria-expanded={showCreateForm}
        >
          {showCreateForm ? 'Cancel' : '+ Create Template'}
        </button>
      </div>
      {portal && (
        <p className="templates-portal-note">
          NCMB has allowed you to manage the firm’s ticket templates. Changes you make are seen by
          everybody who raises a ticket from one, and each card says who changed it last.
        </p>
      )}

      {notice && <div className="error-message" role="alert">{notice}</div>}

      {showCreateForm && (
        <form onSubmit={handleCreateTemplate} className="template-form">
          <h3>Create New Template</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="tt-new-name">Template Name *</label>
              <input
                id="tt-new-name"
                type="text"
                required
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                placeholder="e.g., Password Reset, VPN Setup"
              />
            </div>

            <div className="form-group">
              <label htmlFor="tt-new-desc">Description</label>
              <textarea
                id="tt-new-desc"
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                placeholder="Brief description of the template..."
                rows="3"
              ></textarea>
            </div>

            <div className="form-group">
              <label htmlFor="tt-new-cat">Default Category</label>
              <select
                id="tt-new-cat"
                value={newTemplate.categoryId}
                onChange={(e) => setNewTemplate({ ...newTemplate, categoryId: e.target.value })}
              >
                <option value="">Select category...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tt-new-pri">Default Priority</label>
              <select
                id="tt-new-pri"
                value={newTemplate.priorityId}
                onChange={(e) => setNewTemplate({ ...newTemplate, priorityId: e.target.value })}
              >
                <option value="">Select priority...</option>
                {priorities.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tt-new-appr">Default Approver</label>
              <select
                id="tt-new-appr"
                value={newTemplate.defaultApproverId}
                onChange={(e) => setNewTemplate({ ...newTemplate, defaultApproverId: e.target.value })}
              >
                <option value="">Select approver...</option>
                {approvers.map((u) => (
                  <option key={u.id} value={u.id}>{approverName(u)}</option>
                ))}
              </select>
            </div>
          </div>

          <button type="submit" className="btn btn-success">
            Create Template
          </button>
        </form>
      )}

      {editing && (
        <div className="template-edit">
          <h4>Edit template</h4>
          <div className="template-edit-grid">
            <label>
              <span>Name</span>
              <input value={editing.name}
                     onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </label>
            <label>
              <span>Category</span>
              <select value={editing.categoryId}
                      onChange={(e) => setEditing({ ...editing, categoryId: e.target.value })}>
                <option value="">None</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>
              <span>Priority</span>
              <select value={editing.priorityId}
                      onChange={(e) => setEditing({ ...editing, priorityId: e.target.value })}>
                <option value="">None</option>
                {priorities.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>
              <span>Default approver</span>
              <select value={editing.defaultApproverId}
                      onChange={(e) => setEditing({ ...editing, defaultApproverId: e.target.value })}>
                <option value="">None</option>
                {approvers.map((u) => (
                  <option key={u.id} value={u.id}>{approverName(u)}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="template-edit-full">
            <span>Description and checklist</span>
            <textarea rows={8} value={editing.description}
                      onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          </label>
          <div className="template-actions">
            <button type="button" className="btn btn-primary" onClick={saveEdit}>Save changes</button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="templates-grid">
        {loading ? (
          <p>Loading templates...</p>
        ) : templates.length === 0 ? (
          <p className="no-templates">No templates yet. Create one to get started!</p>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="template-card" data-template={template.name}>
              <div className="template-header">
                <h4>{template.name}</h4>
                {template.priority_name && (
                  <span className="priority-badge" data-priority={prioritySlug(template.priority_name)}>
                    {template.priority_name}
                  </span>
                )}
              </div>

              {template.description && (
                <p className="template-description">{template.description}</p>
              )}

              <div className="template-details">
                {template.category_name && (
                  <div className="detail-item">
                    <span className="label">Category:</span>
                    <span className="value">{template.category_name}</span>
                  </div>
                )}
                {template.approver_name && (
                  <div className="detail-item">
                    <span className="label">Default Approver:</span>
                    <span className="value">{template.approver_name}</span>
                  </div>
                )}
                {template.updated_by_name && (
                  <div className="detail-item">
                    <span className="label">Last changed by:</span>
                    <span className="value">{template.updated_by_name}</span>
                  </div>
                )}
              </div>

              {retiring === template.id ? (
                <div className="template-retire" role="group" aria-label={`Retire ${template.name}?`}>
                  <p>Retire “{template.name}”? It stops appearing on the create form. Tickets already raised from it are unaffected.</p>
                  <div className="template-actions">
                    <button type="button" className="btn btn-danger btn-small" onClick={() => retire(template)}>Retire</button>
                    <button type="button" className="btn btn-secondary btn-small" onClick={() => setRetiring(null)}>Keep it</button>
                  </div>
                </div>
              ) : (
                <div className="template-actions">
                  {!portal && (
                    <button type="button" className="btn btn-primary" onClick={() => handleUseTemplate(template.id)}>
                      Use Template
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => startEdit(template)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-danger btn-small" onClick={() => setRetiring(template.id)}>
                    Retire
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TicketTemplates;
